import React, { useEffect, useState, useMemo } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { getProviderBiomarkers } from "../../../../common/network/Api";
import SearchBar from "../common/SearchBar";
import FilterSelect from "../prescriptionTab/FilterSelect";
import CheckedIcon from "../../svg/CheckedIcon";
import UncheckedIcon from "../../svg/UncheckedIcon";
import { Skeleton } from "@mui/material";
import { enPrecisionTestKind, enBiomarkerCategory } from "../../store/helper";

// Shape returned by GetProviderBiomarkers. `id` is the PNOĒ biomarker id and is
// what we send as `biomarkerid` when saving the panel — there is no separate
// biomarkerID/providerBiomarkerID on this endpoint. `price` isn't returned yet
// (kept optional for when the backend adds it).
export interface ProviderBiomarker {
  id: string;
  name: string;
  shortname?: string;
  alternativenamE1?: string;
  testKind: number;
  category: number | null;
  unit?: string;
  code?: string;
  price?: number;
  // LOINC code for the test, e.g. "53835-5".
  loinc?: string;
  // The lab's own id for this biomarker. Sent along with `biomarkerid` when
  // saving a panel so the backend can resolve the lab-side record (and with it
  // the ask-at-order-entry questions) without a second catalog lookup.
  labBiomarkerID?: string | null;
}

/** One item of a custom panel as `UpdateUserLabPanel` takes it. */
export interface LabPanelItemInput {
  biomarkerid: string;
  labBiomarkerID?: string | null;
}

const getCategoryLabel = (biomarker: ProviderBiomarker): string =>
  typeof biomarker.category === "number"
    ? String(enBiomarkerCategory[biomarker.category] ?? biomarker.category)
    : String(biomarker.category ?? "");

export const validateStep2 = (
  selectedItems: LabPanelItemInput[]
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (selectedItems.length === 0) {
    errors.push("Please select at least one biomarker");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

interface LabBuilderStep2Props {
  provider: string | null;
  selectedItems: LabPanelItemInput[];
  setSelectedItems: (items: LabPanelItemInput[]) => void;
}

const LabBuilderStep2: React.FC<LabBuilderStep2Props> = ({
  provider,
  selectedItems,
  setSelectedItems,
}) => {
  const theme: Theme = useTheme();
  const [biomarkers, setBiomarkers] = useState<ProviderBiomarker[]>([]);
  const [isLoading, setIsLoading] = useState(!!provider);
  const [searchFilter, setSearchFilter] = useState("");
  // Test type is now a client-side filter here (the full provider list is
  // fetched once); empty = show all. Options are derived from the biomarkers
  // actually present, so there are never empty test-type selections.
  const [selectedTestTypes, setSelectedTestTypes] = useState<string[]>([]);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 900);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 900);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setSearchFilter("");
    setSelectedTestTypes([]);

    const fetchBiomarkers = async () => {
      if (!provider) return;
      setIsLoading(true);
      try {
        // Fetch the full provider biomarker list (no kind/test-type filter).
        // Test type is applied as a client-side filter in a later step now.
        const response = await getProviderBiomarkers(provider);
        if (response && Array.isArray(response)) {
          setBiomarkers(response);
        } else {
          setBiomarkers([]);
        }
      } catch (error) {
        console.error("Error fetching provider biomarkers:", error);
        setBiomarkers([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBiomarkers();
  }, [provider]);

  // Test-type filter options: only the kinds actually present in the fetched
  // list (mapped to their labels), so every option yields biomarkers.
  const testTypeOptions = useMemo(() => {
    const unique = new Set<number>();
    biomarkers.forEach((b) => {
      if (b.testKind !== null && b.testKind !== undefined) {
        unique.add(b.testKind);
      }
    });
    return Array.from(unique)
      .map((k) => ({
        key: String(k),
        label: enPrecisionTestKind[k] || String(k),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [biomarkers]);

  // Apply the search (name or LOINC code — matching the category label too
  // caused confusing results, e.g. "ho" matched every "Pathogens" biomarker;
  // LOINC codes are digits plus a dash, so they can't collide with name text)
  // and the test-type filter (empty selection = all types).
  const filteredBiomarkers = useMemo(() => {
    const search = searchFilter.trim().toLowerCase();
    return biomarkers.filter((b) => {
      const matchesSearch =
        !search ||
        (typeof b.name === "string" && b.name.toLowerCase().includes(search)) ||
        (typeof b.loinc === "string" && b.loinc.toLowerCase().includes(search));
      const matchesType =
        selectedTestTypes.length === 0 ||
        selectedTestTypes.includes(String(b.testKind));
      return matchesSearch && matchesType;
    });
  }, [biomarkers, searchFilter, selectedTestTypes]);

  const selectedIds = useMemo(
    () => new Set(selectedItems.map((item) => item.biomarkerid)),
    [selectedItems]
  );

  const selectedBiomarkers = useMemo(
    () => biomarkers.filter((b) => selectedIds.has(b.id)),
    [biomarkers, selectedIds]
  );

  const totalCost = useMemo(
    () => selectedBiomarkers.reduce((sum, b) => sum + (b.price || 0), 0),
    [selectedBiomarkers]
  );

  // Group selected biomarkers by category, sort alphabetically, category 0 at end
  const groupedByCategory = useMemo(() => {
    const groups: Record<string, ProviderBiomarker[]> = {};
    selectedBiomarkers.forEach((b) => {
      const key = String(b.category ?? "0");
      if (!groups[key]) groups[key] = [];
      groups[key].push(b);
    });

    return Object.entries(groups).sort(([a], [b]) => {
      if (a === "0") return 1;
      if (b === "0") return -1;
      return a.localeCompare(b);
    });
  }, [selectedBiomarkers]);

  const toggleBiomarker = (biomarker: ProviderBiomarker) => {
    if (selectedIds.has(biomarker.id)) {
      setSelectedItems(
        selectedItems.filter((item) => item.biomarkerid !== biomarker.id)
      );
    } else {
      setSelectedItems([
        ...selectedItems,
        {
          biomarkerid: biomarker.id,
          labBiomarkerID: biomarker.labBiomarkerID ?? null,
        },
      ]);
    }
  };

  const skeletonBg = theme.colorTokens.N[30];

  if (isLoading) {
    return (
      <div style={styles.container(isSmallScreen)}>
        {/* Left panel skeleton */}
        <div style={styles.leftPanel(theme)}>
          <Skeleton
            variant="rectangular"
            sx={{
              width: "160px",
              height: "20px",
              bgcolor: skeletonBg,
              borderRadius: "4px",
            }}
            animation="pulse"
          />
          <Skeleton
            variant="rectangular"
            sx={{
              width: "100%",
              height: "40px",
              bgcolor: skeletonBg,
              borderRadius: "8px",
            }}
            animation="pulse"
          />
          <div style={styles.biomarkerList}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                sx={{
                  width: "100%",
                  height: "54px",
                  bgcolor: skeletonBg,
                  borderRadius: "8px",
                }}
                animation="pulse"
              />
            ))}
          </div>
        </div>
        {/* Right panel skeleton */}
        <div style={styles.rightPanel(theme, isSmallScreen)}>
          <div style={styles.selectedHeader}>
            <Skeleton
              variant="rectangular"
              sx={{
                width: "80px",
                height: "20px",
                bgcolor: skeletonBg,
                borderRadius: "4px",
              }}
              animation="pulse"
            />
            <Skeleton
              variant="rectangular"
              sx={{
                width: "100px",
                height: "28px",
                bgcolor: skeletonBg,
                borderRadius: "8px",
              }}
              animation="pulse"
            />
          </div>
          <Skeleton
            variant="rectangular"
            sx={{
              width: "140px",
              height: "16px",
              bgcolor: skeletonBg,
              borderRadius: "4px",
              marginBottom: "8px",
            }}
            animation="pulse"
          />
          <Skeleton
            variant="rectangular"
            sx={{
              width: "100%",
              height: "14px",
              bgcolor: skeletonBg,
              borderRadius: "4px",
            }}
            animation="pulse"
          />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container(isSmallScreen)}>
      {/* Left panel - Available biomarkers */}
      <div style={styles.leftPanel(theme)}>
        <div style={styles.panelTitle(theme)}>Biomarkers</div>
        <div style={styles.filtersRow}>
          <div style={styles.searchWrapper}>
            <SearchBar
              searchFilter={searchFilter}
              setSearchFilter={setSearchFilter}
              placeholder="Search"
              customHeight="42px"
            />
          </div>
          <FilterSelect
            title="Test type"
            options={testTypeOptions}
            selectedKeys={selectedTestTypes}
            onSelectionChange={setSelectedTestTypes}
          />
        </div>
        {/* Column header — carries the "LOINC"/"Price" labels so the rows
            below don't repeat them. Widths mirror the row columns. Hidden on
            small screens, where the cards stack and have no columns to label. */}
        {!isSmallScreen && (
          <div style={styles.listHeader(theme)}>
            <div style={styles.checkbox} />
            <span style={styles.colName}>Biomarker</span>
            <span style={styles.colLoinc}>LOINC</span>
            <span style={styles.colPrice}>Price</span>
          </div>
        )}
        <div style={styles.biomarkerList}>
          {filteredBiomarkers.map((biomarker) => (
            <div
              key={biomarker.id}
              style={
                selectedIds.has(biomarker.id)
                  ? styles.biomarkerRowSelected(theme)
                  : styles.biomarkerRow(theme)
              }
              onClick={() => toggleBiomarker(biomarker)}
            >
              <div style={styles.checkbox}>
                {selectedIds.has(biomarker.id) ? (
                  <CheckedIcon fill={theme.colors_ds.PRIMARY_ICON_MAIN} />
                ) : (
                  <UncheckedIcon fill={theme.colors_ds.NEUTRAL_ICON_DEFAULT} />
                )}
              </div>
              {isSmallScreen ? (
                // Narrow screens can't hold three columns — the name column
                // ends up so thin it wraps one word per line. Stack instead:
                // name with category + LOINC beneath it, price on the right.
                <>
                  <div style={styles.stackedInfo}>
                    <span style={styles.biomarkerName(theme)}>
                      {biomarker.name}
                    </span>
                    <span style={styles.stackedMeta(theme)}>
                      {[
                        getCategoryLabel(biomarker),
                        biomarker.loinc && `LOINC ${biomarker.loinc}`,
                      ]
                        .filter(Boolean)
                        .join("  \u00b7  ")}
                    </span>
                  </div>
                  {/* Sibling of the text block (not inside it) so the row's
                      own align-items centers the price against the card. */}
                  <span style={styles.cellPrice(theme, {})}>
                    ${biomarker.price ?? 0}
                  </span>
                </>
              ) : (
                <>
                  <div style={styles.colName}>
                    <span style={styles.biomarkerName(theme)}>
                      {biomarker.name}
                    </span>
                    <span style={styles.biomarkerCategory(theme)}>
                      {getCategoryLabel(biomarker)}
                    </span>
                  </div>
                  <span style={styles.cellMeta(theme, styles.colLoinc)}>
                    {biomarker.loinc || "\u2014"}
                  </span>
                  <span style={styles.cellPrice(theme, styles.colPrice)}>
                    ${biomarker.price ?? 0}
                  </span>
                </>
              )}
            </div>
          ))}
          {filteredBiomarkers.length === 0 && !isLoading && (
            <div style={styles.emptyMessage(theme)}>No biomarkers found</div>
          )}
        </div>
      </div>

      {/* Right panel - Selected biomarkers */}
      <div style={styles.rightPanel(theme, isSmallScreen)}>
        <div style={styles.selectedHeader}>
          <div style={styles.panelTitle(theme)}>Selected</div>
          <div style={styles.badge(theme)}>
            {selectedItems.length} biomarker
            {selectedItems.length !== 1 ? "s" : ""}
          </div>
        </div>

        <div style={styles.selectedList}>
          {selectedBiomarkers.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyStateTitle(theme)}>
                No biomarkers selected
              </div>
              <div style={styles.emptyStateText(theme)}>
                Select from the list on the left
              </div>
            </div>
          ) : (
            groupedByCategory.map(([category, items]) => (
              <div key={category} style={styles.categoryGroup}>
                <div style={styles.categoryHeader(theme)}>
                  {enBiomarkerCategory[Number(category)] ?? category}
                </div>
                <div style={styles.categoryBiomarkers(theme)}>
                  {items.map((b) => b.name).join(", ")}
                </div>
              </div>
            ))
          )}
        </div>

        <div style={styles.divider(theme)} />
        <div style={styles.totalRow}>
          <span style={styles.totalLabel(theme)}>Total cost</span>
          <span style={styles.totalValue(theme)}>${totalCost.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
};

export default LabBuilderStep2;

const styles = {
  container: (isSmallScreen: boolean) => ({
    display: "flex",
    flexDirection: isSmallScreen ? ("column" as "column") : ("row" as "row"),
    gap: "24px",
    flex: 1,
    minHeight: 0,
  }),
  leftPanel: (theme: Theme) => ({
    flex: 2,
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "12px",
    minHeight: 0,
  }),
  rightPanel: (theme: Theme, isSmallScreen: boolean) => ({
    width: isSmallScreen ? "100%" : "",
    flex: 1,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column" as "column",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "12px 16px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    alignSelf: "flex-start" as "flex-start",
    maxHeight: isSmallScreen ? "none" : "100%",
    overflow: "auto" as "auto",
  }),
  panelTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  filtersRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  searchWrapper: {
    flex: 1,
    minWidth: 0,
  },
  biomarkerList: {
    flex: 1,
    overflowY: "auto" as "auto",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "12px",
    scrollbarWidth: "thin" as "thin",
    scrollbarColor: "#c5c8cc transparent",
  },
  biomarkerRow: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    cursor: "pointer",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    minHeight: "54px",
    flexShrink: 0,
  }),
  biomarkerRowSelected: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px",
    border: `1px solid ${theme.colorTokens.P[80]}`,
    borderRadius: "8px",
    cursor: "pointer",
    backgroundColor: theme.colorTokens.P[10],
    minHeight: "54px",
    flexShrink: 0,
  }),
  // Fixed width so the header's empty first cell lines up with the checkboxes.
  checkbox: {
    width: "20px",
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  // Header row for the biomarker table. Same padding/gap as a row (minus the
  // border, hence the extra 1px) so the labels sit over their columns.
  listHeader: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "0 9px",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    flexShrink: 0,
  }),
  // Column widths, shared by the header and the rows.
  colName: {
    flex: 1,
    minWidth: 0,
  },
  colLoinc: {
    width: "65px",
    flexShrink: 0,
  },
  colPrice: {
    width: "70px",
    flexShrink: 0,
    textAlign: "right" as "right",
  },
  // Name and category share the Biomarker column, as they did before the
  // table layout — the category reads as a qualifier of the name, not a field.
  biomarkerName: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  biomarkerCategory: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    marginLeft: "8px",
  }),
  cellMeta: (theme: Theme, colStyle: Record<string, any>) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    overflow: "hidden" as "hidden",
    textOverflow: "ellipsis" as "ellipsis",
    whiteSpace: "nowrap" as "nowrap",
    ...colStyle,
  }),
  // Stacked (small-screen) card body.
  stackedInfo: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column" as "column",
    gap: "2px",
  },
  stackedMeta: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  cellPrice: (theme: Theme, colStyle: Record<string, any>) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    whiteSpace: "nowrap" as "nowrap",
    flexShrink: 0,
    ...colStyle,
  }),
  selectedHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px",
  },
  badge: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    backgroundColor: theme.colorTokens.N[30],
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "4px 8px",
  }),
  selectedList: {
    flex: 1,
    overflowY: "auto" as "auto",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
    scrollbarWidth: "thin" as "thin",
    scrollbarColor: "#c5c8cc transparent",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    padding: "12px",
    flex: 1,
  },
  emptyStateTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  emptyStateText: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "24px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    textAlign: "center" as "center",
  }),
  categoryGroup: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "2px",
  },
  categoryHeader: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  categoryBiomarkers: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  divider: (theme: Theme) => ({
    height: "1px",
    backgroundColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
    margin: "12px 0",
  }),
  totalRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    justifyContent: "space-between",
  },
  totalLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    letterSpacing: "-0.28px",
    whiteSpace: "nowrap",
  }),
  totalValue: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "27px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  emptyMessage: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    textAlign: "center" as "center",
    padding: "24px",
  }),
};
